import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReportBookingContext } from '../models/bookingContext';
import { formDefinitions, type FieldErrors, type FormId, type FormValue, validateSubmission } from '../models/forms';

type Status = 'idle' | 'submitting' | 'success' | 'error';

function idempotencyStorageKey(formId: FormId): string {
  return `dulanstar:form-idempotency:${formId}`;
}

function submissionId(formId: FormId): string {
  const key = idempotencyStorageKey(formId);
  try {
    const existing = window.sessionStorage.getItem(key);
    if (existing) return existing;
    const created = window.crypto.randomUUID();
    window.sessionStorage.setItem(key, created);
    return created;
  } catch {
    return window.crypto.randomUUID();
  }
}

function clearSubmissionId(formId: FormId): void {
  try {
    window.sessionStorage.removeItem(idempotencyStorageKey(formId));
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
}

export function useFormPresenter(formId: FormId) {
  const definition = formDefinitions[formId];
  const initialData = useMemo(() => Object.fromEntries(definition.fields.filter((field) => field.type !== 'section').map((field) => [field.id, field.type === 'checkbox' ? [] : field.initialValue ?? ''])) as Record<string, FormValue>, [definition]);
  const [data, setData] = useState<Record<string, FormValue>>(initialData);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [bookingContext, setBookingContext] = useState<ReportBookingContext | null>(null);
  const idempotencyKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (formId !== 'remittance') return;
    const controller = new AbortController();
    void fetch('/api/forms/remittance/context', { signal: controller.signal })
      .then((response) => response.json() as Promise<{ context?: ReportBookingContext | null }>)
      .then(({ context }) => {
        if (!context) return;
        setBookingContext(context);
        setData((current) => ({
          ...current,
          bookingName: typeof current.bookingName === 'string' && !current.bookingName
            ? context.guestName
            : current.bookingName,
        }));
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === 'AbortError')) setBookingContext(null);
      });
    return () => controller.abort();
  }, [formId]);

  const setValue = useCallback((id: string, value: FormValue) => {
    setData((current) => ({ ...current, [id]: value }));
    setErrors((current) => {
      if (!current[id]) return current;
      const next = { ...current };
      delete next[id];
      return next;
    });
  }, []);

  const submit = useCallback(async (getTurnstileToken?: () => Promise<string>) => {
    const validated = validateSubmission(formId, { data, companyWebsite });
    if (!validated.success) {
      setErrors(validated.fieldErrors);
      setStatus('error');
      setMessage('請檢查標示的欄位。Please review the highlighted fields.');
      return;
    }

    setStatus('submitting');
    setMessage('');
    let turnstileToken = '';
    try {
      turnstileToken = await getTurnstileToken?.() ?? '';
    } catch {
      setStatus('error');
      setMessage('安全驗證暫時無法完成，請再試一次。Security check unavailable, please try again.');
      return;
    }
    const idempotencyKey = idempotencyKeyRef.current ?? submissionId(formId);
    idempotencyKeyRef.current = idempotencyKey;
    try {
      const response = await fetch(`/api/forms/${formId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
        body: JSON.stringify({ data: validated.data, companyWebsite, turnstileToken }),
      });
      const result = await response.json() as {
        ok: boolean;
        deliveryMode?: string;
        code?: string;
        fieldErrors?: FieldErrors;
        received?: boolean;
        notificationStatus?: 'sent' | 'failed' | 'pending';
      };
      if (!response.ok || !result.ok) {
        if (result.code === 'IDEMPOTENCY_CONFLICT') {
          clearSubmissionId(formId);
          idempotencyKeyRef.current = null;
        }
        setErrors(result.fieldErrors ?? {});
        setStatus('error');
        setMessage(result.code === 'RATE_LIMITED'
          ? '送出次數過多，請稍後再試。Please try again later.'
          : result.code === 'TURNSTILE_FAILED' || result.code === 'TURNSTILE_UNAVAILABLE'
            ? '安全驗證暫時無法完成，請再試一次。Security check unavailable, please try again.'
          : result.code === 'RECORD_NOT_SAVED'
            ? '資料尚未接收，請稍後再試；再次送出不會建立重複紀錄。'
            : result.code === 'IDEMPOTENCY_CONFLICT'
              ? '這次填寫內容已變更，請確認後再按一次送出。'
              : '目前無法送出，請稍後再試。');
        return;
      }
      clearSubmissionId(formId);
      idempotencyKeyRef.current = null;
      setStatus('success');
      setMessage(result.code === 'NOTIFICATION_DELAYED'
        ? `${definition.success}\n\n資料已安全接收；通知信目前延遲，不需要再次送出，我們會由紀錄補送。`
        : result.deliveryMode === 'console'
          ? `${definition.success}\n\n資料已接收，但目前為測試模式，尚未寄出 Email。`
          : definition.success);
      setData(initialData);
    } catch {
      setStatus('error');
      setMessage('網路連線失敗，請稍後再試。Network error, please try again.');
    }
  }, [companyWebsite, data, definition.success, formId, initialData]);

  return { definition, data, errors, status, message, bookingContext, companyWebsite, setCompanyWebsite, setValue, submit };
}
